import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
 
import axios from 'axios';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Tag,
  Card,
  Popconfirm,
  message,
  ConfigProvider,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { API_BASE_URL } from '../components/l1';

// ====================
 

const categoryApi = {
  getAll: () => axios.get(`${API_BASE_URL}/categories`).then(res => res.data),
  create: (data) => axios.post(`${API_BASE_URL}/categories`, data).then(res => res.data),
  update: (id, data) => axios.put(`${API_BASE_URL}/categories/${id}`, data).then(res => res.data),
  delete: (id) => axios.delete(`${API_BASE_URL}/categories/${id}`).then(res => res.data),
};

 

 
const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryApi.getAll(),
    select: (data) => data.data || [],
  });
};

const useCreateCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => categoryApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      message.success('Category created successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to create category');
    },
  });
};

const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => categoryApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      message.success('Category updated successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to update category');
    },
  });
};

const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => categoryApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      message.success('Category deleted successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to delete category');
    },
  });
};

// ====================
// 4. FORM COMPONENT
// ====================
const CategoryForm = ({ open, onClose, editingCategory, onSuccess }) => {
  const [form] = Form.useForm();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  
  const [loading, setLoading] = useState(false);

  // Set form values when editing
  React.useEffect(() => {
    if (editingCategory && open) {
      form.setFieldsValue({
        name: editingCategory.name,
        slug: editingCategory.slug,
      });
    } else {
      form.resetFields();
    }
  }, [editingCategory, open, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (editingCategory) {
        await updateMutation.mutateAsync({ id: editingCategory._id, data: values });
      } else {
        await createMutation.mutateAsync(values);
      }
      onSuccess();
      onClose();
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    if (!editingCategory) {
      const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      form.setFieldsValue({ slug });
    }
  };

  return (
    <Modal
      title={editingCategory ? "Edit Category" : "Add New Category"}
      open={open}
      onCancel={onClose}
      footer={null}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ name: '', slug: '' }}
      >
        <Form.Item
          name="name"
          label="Category Name"
          rules={[
            { required: true, message: 'Please enter category name' },
            { min: 2, message: 'Minimum 2 characters' },
            { max: 50, message: 'Maximum 50 characters' }
          ]}
        >
          <Input 
            placeholder="e.g., Roses, Chocolates" 
            size="large"
            onChange={handleNameChange}
          />
        </Form.Item>

        <Form.Item
          name="slug"
          label="Slug"
          rules={[
            { required: true, message: 'Please enter slug' },
            { 
              pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
              message: 'Only lowercase letters, numbers, and hyphens'
            }
          ]}
          tooltip="URL-friendly version of the name"
        >
          <Input 
            placeholder="e.g., roses, mixed-flowers" 
            size="large"
          />
        </Form.Item>

        <Form.Item className="mb-0">
          <div className="flex justify-end gap-3">
            <Button onClick={onClose} size="large">
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large"
              loading={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ====================
// 5. MAIN COMPONENT
// ====================
const CategoryManagement = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchText, setSearchText] = useState('');

  // TanStack Query hooks
  const { data: categories = [], isLoading, error } = useCategories();
  const deleteMutation = useDeleteCategory();

  // Filter categories based on search
  const filteredCategories = categories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchText.toLowerCase()) ||
      category.slug.toLowerCase().includes(searchText.toLowerCase())
  );

  // Handle edit
  const handleEdit = (category) => {
    setEditingCategory(category);
    setModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    await deleteMutation.mutateAsync(id);
  };

  // Close modal
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCategory(null);
  };

  // Success callback
  const handleSuccess = () => {
    setEditingCategory(null);
  };

  // Columns for Ant Design Table
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
          <span className="font-medium text-gray-800">{text}</span>
        </div>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (text) => (
        <Tag color="blue" className="font-mono text-sm">
          {text}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            className="bg-blue-500 hover:bg-blue-600"
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this category?"
            description="Are you sure you want to delete this category?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingOutlined className="text-4xl text-blue-500 mb-4" spin />
          <p className="text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Categories</h3>
          <p className="text-gray-600 mb-4">Failed to fetch categories from server</p>
          <Button 
            type="primary" 
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            Category Management
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Manage product categories with name and slug
          </p>
        </div>

        {/* Stats */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-md hover:shadow-lg transition-all duration-300">
              <div className="text-2xl font-bold text-blue-600">{categories.length}</div>
              <div className="text-gray-600">Total Categories</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-md hover:shadow-lg transition-all duration-300">
              <div className="text-2xl font-bold text-green-600">
                {filteredCategories.length}
              </div>
              <div className="text-gray-600">Filtered</div>
            </Card>
          </Col>
        </Row>

        {/* Search and Add Button */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="w-full md:w-1/3">
            <Input
              placeholder="Search category name or slug..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
              className="w-full shadow-sm rounded-lg"
              allowClear
            />
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingCategory(null);
              setModalOpen(true);
            }}
            size="large"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg w-full md:w-auto"
          >
            Add New Category
          </Button>
        </div>

        {/* Categories Table */}
        <Card className="shadow-xl rounded-xl overflow-hidden border-0">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-800">All Categories</h2>
            <p className="text-gray-500 text-sm">List of all categories with actions</p>
          </div>
          
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              dataSource={filteredCategories}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} categories`,
                className: 'px-4'
              }}
              className="category-table"
              size="middle"
              scroll={{ x: true }}
            />
          </div>
        </Card>

        {/* Form Modal */}
        <CategoryForm
          open={modalOpen}
          onClose={handleModalClose}
          editingCategory={editingCategory}
          onSuccess={handleSuccess}
        />
      </div>
    </div>
  );
};

// ====================
// 6. STYLES (Inline)
// ====================
const styles = `
  /* Mobile Responsive */
  @media (max-width: 768px) {
    .category-table .ant-table-thead > tr > th,
    .category-table .ant-table-tbody > tr > td {
      padding: 12px 8px !important;
    }
    
    .category-table .ant-table {
      font-size: 14px;
    }
    
    .ant-modal {
      max-width: 95vw;
      margin: 10px auto;
    }
  }
  
  @media (max-width: 480px) {
    .category-table .ant-btn {
      padding: 4px 8px;
      font-size: 12px;
    }
    
    .ant-tag {
      font-size: 11px;
    }
  }
  
  /* Table Hover Effects */
  .category-table .ant-table-tbody > tr:hover > td {
    background-color: #f0f9ff !important;
  }
  
  /* Card Hover */
  .ant-card {
    transition: all 0.3s ease;
  }
  
  .ant-card:hover {
    transform: translateY(-2px);
  }
  
  /* Button Animations */
  .ant-btn {
    transition: all 0.3s ease !important;
  }
  
  .ant-btn:hover {
    transform: translateY(-1px);
  }
  
  /* Input Focus */
  .ant-input:focus,
  .ant-input-focused {
    border-color: #1890ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
  }
  
  /* Custom Scrollbar */
  .overflow-x-auto::-webkit-scrollbar {
    height: 6px;
  }
  
  .overflow-x-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  .overflow-x-auto::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  
  .overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;


export default CategoryManagement;